/**
 * Integrity primitives of the transfer protocol.
 *
 * Two levels, by design:
 * - **CRC32 per chunk** (cheap, synchronous): detects corruption of an
 *   individual frame on the spot — it serves to abort/retry without waiting for the end
 *   and is what makes "per-chunk" integrity verifiable in the tests with
 *   loopback channels (where there is no DTLS to guarantee bit-for-bit).
 * - **SHA-256 per file** (WebCrypto): cryptographic seal of the complete
 *   file, verified at the end of each one. It is the strong guarantee that what is
 *   received == what was sent.
 */

const CRC32_TABLE: Uint32Array = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) === 1 ? 0xed_b8_83_20 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

/** CRC32 (IEEE) of a buffer; unsigned 32-bit integer. */
export function crc32(bytes: Uint8Array): number {
  let crc = 0xff_ff_ff_ff;
  for (const byte of bytes) {
    crc = (CRC32_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xff_ff_ff_ff) >>> 0;
}

/** Copies to a fresh `ArrayBuffer` (satisfies `BufferSource` without assertions). */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.length);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

/** Returns the environment's `SubtleCrypto` (browser, Node ≥20, bun). */
function subtle(): SubtleCrypto {
  const cryptoObj = globalThis.crypto;
  if (!cryptoObj || !cryptoObj.subtle) {
    throw new Error("WebCrypto SubtleCrypto is not available in this environment");
  }
  return cryptoObj.subtle;
}

/** Streaming SHA-256 accumulator WITHOUT loading the whole file into memory. */
export interface Sha256Streaming {
  update: (chunk: Uint8Array) => void;
  digestHex: () => Promise<string>;
}

/**
 * "Poor man's" incremental SHA-256: WebCrypto does not expose an incremental hash, so
 * we accumulate references to the chunks (not copies) and digest at the end.
 * Since the `FileSource` delivers windows and the sink writes them as they come, the memory
 * peak is marked by the consumer, not this accumulator (it keeps views, it does not
 * duplicate the bytes). For huge files in the real browser the
 * File System Access API in the sink is advisable; the hash can be recomputed by reading from the
 * sink in windows. Documented as an improvement in the guide.
 */
export function createSha256(): Sha256Streaming {
  const parts: Uint8Array[] = [];
  return {
    update(chunk: Uint8Array) {
      parts.push(chunk);
    },
    async digestHex() {
      let total = 0;
      for (const p of parts) {
        total += p.length;
      }
      const joined = new Uint8Array(total);
      let offset = 0;
      for (const p of parts) {
        joined.set(p, offset);
        offset += p.length;
      }
      const digest = await subtle().digest("SHA-256", toArrayBuffer(joined));
      return toHex(new Uint8Array(digest));
    },
  };
}

/** SHA-256 hex of a complete buffer (helper for tests and final verification). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await subtle().digest("SHA-256", toArrayBuffer(bytes));
  return toHex(new Uint8Array(digest));
}

function toHex(bytes: Uint8Array): string {
  let out = "";
  for (const byte of bytes) {
    out += byte.toString(16).padStart(2, "0");
  }
  return out;
}
